import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, BadgeCheck, ArrowRight, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Verified Job Integration (spec 13–16): badge + verified/not-verified apply prompts.
export const VerifiedApplyPrompt = ({ job }) => {
  const token = localStorage.getItem('codearena_token');
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  // 'required' | 'recommended' | 'none' — default to soft recommendation
  const requirement = (job?.verification_requirement || 'recommended').toLowerCase();

  useEffect(() => {
    if (!token) { setStatus({ verified: false }); return; }
    axios.get(`${API}/api/verified-talent/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setStatus({ verified: !!r.data?.profile?.is_verified, vid: r.data?.profile?.verification_id }))
      .catch(() => setStatus({ verified: false }));
  }, [token]);

  if (requirement === 'none' || status === null) return null;

  // Verified candidate
  if (status.verified) {
    return (
      <div className="mt-4 rounded-2xl border-2 border-[#16803C]/25 bg-[#F0FDF4] p-5" data-testid="verified-apply-prompt">
        <div className="flex items-center gap-2 mb-2">
          <BadgeCheck size={18} className="text-[#16803C]" />
          <p className="font-bold text-[#14532D]">You're applying with a Verified Profile</p>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold text-[#16803C] mb-3">
          {['Verified Profile', 'Skills Verified', 'Assessment Completed', 'Credential Available'].map((t) => (
            <span key={t} className="inline-flex items-center gap-1"><CheckCircle size={12} /> {t}</span>
          ))}
        </div>
        <p className="text-sm text-[#166534]">Your verified profile is automatically linked to this application — no need to re-upload documents.
          {status.vid && <> <Link to={`/verify/${status.vid}`} className="underline font-semibold" data-testid="verified-apply-report-link">View my verified report →</Link></>}
        </p>
      </div>
    );
  }

  // Not verified — required (hard) vs recommended (soft)
  const isRequired = requirement === 'required';
  if (dismissed && !isRequired) return null;
  return (
    <div className={`mt-4 rounded-2xl border-2 p-5 ${isRequired ? 'border-[#B91C1C]/30 bg-[#FEF2F2]' : 'border-[#E5E5E5] bg-[#FAFAF9]'}`} data-testid="not-verified-apply-prompt">
      <div className="flex items-center gap-2 mb-1.5">
        <ShieldCheck size={18} className={isRequired ? 'text-[#B91C1C]' : 'text-[#525252]'} />
        <p className="font-bold text-[#0A0A0A]">{isRequired ? 'Verified Profile Required' : 'Your profile is not verified yet'}</p>
      </div>
      <p className="text-sm text-[#525252] mb-3">
        {isRequired
          ? 'This employer requires a verified profile to apply. Get verified to unlock this application.'
          : 'Strengthen your application by completing your EvalentX Verified Profile — verified candidates get discovered faster.'}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/verified-talent" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#B91C1C] hover:bg-[#7F1D1D] text-white text-sm font-bold" data-testid="get-verified-before-apply-btn">
          {isRequired ? 'Get Your Profile Verified' : 'Get Verified Before Applying'} <ArrowRight size={14} />
        </Link>
        {!isRequired && (
          <button onClick={() => setDismissed(true)} className="text-sm font-semibold text-[#525252] hover:text-[#0A0A0A]" data-testid="continue-without-verification-btn">
            Continue Without Verification
          </button>
        )}
      </div>
    </div>
  );
};

// Small badge for job cards / headers
export const VerifiedJobBadge = ({ job, className = '' }) => {
  const requirement = (job?.verification_requirement || 'recommended').toLowerCase();
  if (requirement === 'none') return null;
  const required = requirement === 'required';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${required ? 'bg-[#FEF2F2] text-[#B91C1C] border border-[#FECACA]' : 'bg-[#FAFAF9] text-[#525252] border border-[#E5E5E5]'} ${className}`}
      data-testid="verified-job-badge"
    >
      <ShieldCheck size={11} /> {required ? 'Verified Required' : 'Verified Recommended'}
    </span>
  );
};

export default VerifiedApplyPrompt;
